import { Card, Form, Input, Button, Typography, Row, Col, Spin, Alert, Tag, Space, theme } from 'antd';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined, MailOutlined, CreditCardOutlined, UserOutlined, DollarCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useIntl, IntlShape } from 'react-intl';
import { useState } from 'react';


const { Title, Text, Paragraph } = Typography;

// --- Mock Data ---
const mockBookings: { [key: string]: any } = {
  'BK12345': {
    booking_name: 'BK12345',
    email: 'john@example.com',
    guests: [{ first_name: 'John', last_name: 'Smith' }],
    arrival: '2024-12-15',
    departure: '2024-12-18',
    status: 'CONFIRMED',
    summary: {
      total_charge: 15000,
      deposit: 15000,
      balance: 0,
    },
  },
  'BK67890': {
    booking_name: 'BK67890',
    email: 'jane@example.com',
    guests: [{ first_name: 'Jane', last_name: 'Doe' }],
    arrival: '2024-12-20',
    departure: '2024-12-22',
    status: 'CONFIRMED',
    summary: {
      total_charge: 8000,
      deposit: 2000,
      balance: 6000,
    },
  }
};

const getPaymentStatusDetails = (booking: any, intl: IntlShape) => {
  if (booking.summary.balance <= 0 && booking.summary.deposit > 0) {
    return { status: 'paid', text: intl.formatMessage({ id: 'payment.status.paid' }), icon: <CheckCircleOutlined />, color: 'success' };
  }
  if (booking.summary.balance > 0 && booking.summary.deposit > 0) {
    return { status: 'pending', text: intl.formatMessage({ id: 'payment.status.pending' }), icon: <DollarCircleOutlined />, color: 'warning' };
  }
  return { status: 'unpaid', text: intl.formatMessage({ id: 'payment.status.unpaid' }), icon: <CloseCircleOutlined />, color: 'error' };
}

export default function BookingStatus() {
  const [form] = Form.useForm();
  const intl = useIntl();
  const { token } = theme.useToken();

  // --- Local State Management ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<any | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSearch = (values: { bookingRef: string; email: string; }) => {
    setLoading(true);
    setError(null);
    setSearchResult(null);
    setSubmittedEmail(values.email);

    // Simulate API call
    setTimeout(() => {
      const bookingRefKey = values.bookingRef.toUpperCase();
      const booking = mockBookings[bookingRefKey];

      if (booking && booking.email.toLowerCase() === values.email.toLowerCase()) {
        setSearchResult(booking);
      } else {
        setError(intl.formatMessage({ id: 'booking_status.not_found_description' }));
      }
      setLoading(false);
    }, 1000); // 1-second delay
  };

  const formatDate = (dateString: string) => {
    // As react-intl is set up, it's better to use its date formatting for consistency
    // However, sticking to the previous implementation for now.
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(intl.locale, options);
  };

  const paymentDetails = searchResult && !error ? getPaymentStatusDetails(searchResult, intl) : null;
  const nights = searchResult && !error ? dayjs(searchResult.departure).diff(dayjs(searchResult.arrival), 'day') : 0;

  return (
    <div style={{ background: token.colorBgLayout, padding: '48px 16px', minHeight: '100vh' }}>
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={2} style={{ color: token.colorText }}>{intl.formatMessage({ id: 'booking_status.title' })}</Title>
            <Paragraph type="secondary">{intl.formatMessage({ id: 'booking_status.page_subtitle' })}</Paragraph>
          </div>

          <Card style={{ borderRadius: 16, boxShadow: `0 8px 32px ${token.colorText}1a` }}>
            <Form form={form} onFinish={handleSearch} layout="vertical">
              <Form.Item
                name="bookingRef"
                label={intl.formatMessage({ id: 'booking_status.ref_label' })}
                rules={[{ required: true, message: intl.formatMessage({ id: 'booking_status.ref_required' }) }]}
              >
                <Input
                  prefix={<CalendarOutlined style={{ color: token.colorTextDisabled }} />}
                  placeholder={intl.formatMessage({ id: 'booking_status.ref_placeholder' })}
                  size="large"
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="email"
                label={intl.formatMessage({ id: 'booking_status.email_label' })}
                rules={[{ required: true, type: 'email', message: intl.formatMessage({ id: 'booking_status.email_invalid_rule' }) }]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: token.colorTextDisabled }} />}
                  placeholder={intl.formatMessage({ id: 'booking_status.email_placeholder' })}
                  size="large"
                  allowClear
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SearchOutlined />}
                  size="large"
                  block
                  style={{ border: 'none' }}
                >
                  {intl.formatMessage({ id: 'booking_status.submit_button' })}
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {loading && <div style={{ textAlign: 'center', margin: 24 }}><Spin size="large" /></div>}

          {error && !loading && (
            <Alert
              message={<Text strong>{intl.formatMessage({ id: 'booking_status.not_found_title' })}</Text>}
              description={error}
              type="error"
              showIcon
              icon={<CloseCircleOutlined />}
              style={{ marginTop: 24, borderRadius: 8 }}
            />
          )}

          {searchResult && !error && !loading && (
            <Card style={{ marginTop: 24, borderRadius: 16, boxShadow: `0 8px 32px ${token.colorText}1a` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorder}`, paddingBottom: 16, marginBottom: 16 }}>
                <Title level={4}>{intl.formatMessage({ id: 'booking_status.details_title' })}</Title>
                {paymentDetails && (
                  <Tag icon={paymentDetails.icon} color={paymentDetails.color} style={{ fontSize: 14, padding: '6px 12px' }}>
                    {paymentDetails.text}
                  </Tag>
                )}
              </div>

              <Row gutter={[32, 16]}>
                <Col xs={24} md={12}>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div className="flex items-start gap-3">
                      <CalendarOutlined style={{ color: token.colorPrimary, fontSize: 20, marginTop: 4 }} />
                      <div>
                        <Text type="secondary">{intl.formatMessage({ id: 'booking_status.ref_label' })}</Text>
                        <Title level={5} style={{ margin: 0 }}>{searchResult.booking_name}</Title>
                      </div>
                    </div>
                    {searchResult.guests && searchResult.guests.length > 0 && (
                      <div className="flex items-start gap-3">
                        <UserOutlined style={{ color: token.colorPrimary, fontSize: 20, marginTop: 4 }} />
                        <div>
                          <Text type="secondary">{intl.formatMessage({ id: 'booking_status.guest_name_label' })}</Text>
                          <Title level={5} style={{ margin: 0 }}>{`${searchResult.guests[0].first_name} ${searchResult.guests[0].last_name}`}</Title>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <MailOutlined style={{ color: token.colorPrimary, fontSize: 20, marginTop: 4 }} />
                      <div>
                        <Text type="secondary">{intl.formatMessage({ id: 'booking_status.email_info_label' })}</Text>
                        <Title level={5} style={{ margin: 0 }}>{submittedEmail}</Title>
                      </div>
                    </div>
                  </Space>
                </Col>

                <Col xs={24} md={12}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Card style={{ borderRadius: 8, backgroundColor: token.colorPrimaryBgHover }} bodyStyle={{ padding: 16 }}>
                      <Text type="secondary">{intl.formatMessage({ id: 'booking_status.check_in_label' })}</Text>
                      <Title level={5} style={{ margin: 0 }}>{formatDate(searchResult.arrival)}</Title>
                    </Card>
                    <Card style={{ borderRadius: 8, backgroundColor: token.colorInfoBgHover }} bodyStyle={{ padding: 16 }}>
                      <Text type="secondary">{intl.formatMessage({ id: 'booking_status.check_out_label' })}</Text>
                      <Title level={5} style={{ margin: 0 }}>{formatDate(searchResult.departure)}</Title>
                    </Card>
                    <Card style={{ borderRadius: 8, backgroundColor: token.colorBgContainerDisabled }} bodyStyle={{ padding: 16 }}>
                      <Text type="secondary">{intl.formatMessage({ id: 'booking_status.nights_label' })}</Text>
                      <Title level={5} style={{ margin: 0 }}>{nights} {intl.formatMessage({ id: 'booking_status.nights_unit' })} </Title>
                    </Card>
                  </Space>
                </Col>
              </Row>

              <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: 24, marginTop: 24 }}>
                <div style={{ background: token.colorPrimary, color: token.colorTextLightSolid, borderRadius: 8, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space size="middle">
                    <CreditCardOutlined style={{ fontSize: 24 }} />
                    <Text style={{ color: 'white', fontSize: 18, fontWeight: 500 }}>{intl.formatMessage({ id: 'booking_status.total_amount_label' })}</Text>
                  </Space>
                  <Title level={3} style={{ color: 'white', margin: 0 }}>฿{searchResult.summary.total_charge.toLocaleString()}</Title>
                </div>
              </div>

            </Card>
          )}

        </Col>
      </Row>
    </div>
  );
}